import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

// Fullscreen video lightbox. Opens on a user click, so it can play with sound.
export const VideoLightbox = ({ open, src, poster, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-navy/95 p-4 backdrop-blur-md md:p-10"
          data-testid="video-lightbox"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close video"
            data-testid="video-lightbox-close"
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition-all duration-200 hover:scale-105 hover:border-revo-green hover:text-revo-green"
          >
            <X className="h-5 w-5" />
          </button>
          <motion.video
            key={src}
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            src={src}
            poster={poster}
            autoPlay
            loop
            playsInline
            controls
            onClick={(e) => e.stopPropagation()}
            className="max-h-[86vh] w-auto max-w-[92vw] rounded-2xl border border-white/10 shadow-2xl"
          />
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
